// src/redux/profileSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import ProfileService from "../../services/api/profileService";
import { unauthorizedError } from "../../utils/helpers";
import { setSessionExpired } from "./globalSlice";
import AppSecureStorage from "../../services/secureStorage";

const profile = new ProfileService();
const storage = new AppSecureStorage();

export const getProfileDetails = createAsyncThunk(
  "/profile",
  async (_, { dispatch }) => {
    dispatch(setProfileLoading(true));
    try {
      const response = await profile.getProfileDetailsData();
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      if (error) {
        dispatch(setProfileLoading(false));
        unauthorizedError();
        dispatch(setSessionExpired(true));
      }
      throw error;
    }
  },
);

export const updateProfileDetails = createAsyncThunk(
  "/updateProfileDetails",
  async ({ body, navigate }, { dispatch }) => {
    try {
      await profile.updateProfileDetailsData(body, navigate);
      toast.success("Profile updated successfully");
      return body;
    } catch (error) {
      toast.error(error.data.message || "Failed to update profile");
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      throw error;
    }
  },
);

export const uploadProfilePicture = createAsyncThunk(
  "/uploadProfilePicture",
  async ({ formData, navigate }, { dispatch }) => {
    try {
      const response = await profile.uploadProfilePicture(formData, navigate);
      toast.success("Profile picture uploaded successfully");
      // dispatch(getProfileDetails({ navigate }));
      return response;
    } catch (error) {
      toast.error(error.message.error);
      if (error.response && error.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      throw error;
    }
  },
);

export const deleteProfilePicture = createAsyncThunk(
  "/deleteProfilePicture",
  async ({ navigate }, { dispatch }) => {
    try {
      await profile.deleteProfilePicture(navigate);
      toast.success("Profile picture deleted successfully");
      // dispatch(getProfileDetails({ navigate }));
      return true;
    } catch (error) {
      toast.error(error?.message || "Failed to delete profile picture");
      if (error?.response && error.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      throw error;
    }
  },
);

const profileSlice = createSlice({
  name: "profile",
  initialState: {
    user: null,
    userCredentials: null,
    searchItemProfile: "",
    profileLoading: false,
    pictureUploading: false,
  },
  reducers: {
    setSearchItemProfile: (state, action) => {
      state.searchItemProfile = action.payload;
    },
    setProfileLoading: (state, action) => {
      state.profileLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProfileDetails.pending, (state) => {
        state.profileLoading = true;
      })
      .addCase(getProfileDetails.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.user = action.payload;
        const organisationId = //check in api
          action.payload?.organisationId ||
          action.payload?.data?.organisationId ||
          action.payload?.organizationId ||
          action.payload?.data?.organizationId;
        if (organisationId) {
          storage.set("orgId", organisationId);
        }
      })
      .addCase(getProfileDetails.rejected, (state) => {
        state.profileLoading = false;
      })
      .addCase(updateProfileDetails.pending, (state) => {
        state.profileLoading = true;
      })
      .addCase(updateProfileDetails.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.user = { ...state.user, ...action.payload };
      })
      .addCase(updateProfileDetails.rejected, (state) => {
        state.profileLoading = false;
      })
      .addCase(uploadProfilePicture.pending, (state) => {
        state.pictureUploading = true;
      })
      .addCase(uploadProfilePicture.fulfilled, (state, action) => {
        // state.profileLoading = false;
        state.pictureUploading = false;
        const newPicture =
          action.payload?.picture ||
          action.payload?.data?.picture ||
          action.payload?.url ||
          null;
        if (newPicture) {
          state.user = { ...state.user, picture: newPicture };
        }
      })
      .addCase(uploadProfilePicture.rejected, (state) => {
        state.pictureUploading = false;
      })
      .addCase(deleteProfilePicture.pending, (state) => {
        state.pictureUploading = true;
      })
      .addCase(deleteProfilePicture.fulfilled, (state, action) => {
        state.pictureUploading = false;
        if (state.user) {
          state.user = { ...state.user, picture: null };
        }
      })
      .addCase(deleteProfilePicture.rejected, (state) => {
        state.pictureUploading = false;
      });
  },
});

export const { setUserCredentials, setSearchItemProfile, setProfileLoading } =
  profileSlice.actions;

export default profileSlice.reducer;
